/**
 * Module ID enum for type-safe module identification
 * Keep in sync with backend ModuleType enum
 */
export enum ModuleId {
  CAPABILITIES_V1 = 'CAPABILITIES_V1',
  CAPABILITIES_V2 = 'CAPABILITIES_V2',
  REPORTING = 'REPORTING',
  CHAT = 'CHAT',
  NOTIFICATIONS = 'NOTIFICATIONS',
  EXPORTS = 'EXPORTS',
  INTEGRATIONS = 'INTEGRATIONS',
  INVOICES = 'INVOICES',
  LANDING_PAGE = 'LANDING_PAGE',
  DOCUMENTS = 'DOCUMENTS',
  PEER_BENCHMARKS = 'PEER_BENCHMARKS',
  TEAM_MANAGEMENT = 'TEAM_MANAGEMENT',
  /**
   * TEMPORARY (buyer-marketplace limited release, PRO-646). Enforced entirely
   * server-side — deliberately NOT wired to `requiredModule` or any route, so
   * marketplace setup surfaces stay reachable when it is off. Producers are
   * `Basic` users with no `active_vendor`, so `isActiveModule` cannot see
   * their processor's copy of this module; the block surfaces as an error
   * toast on the failed publish. Remove at general availability; see
   * `packages/api/src/services/producers/BUYER_MARKETPLACE_MODULE.md`.
   */
  BUYER_MARKETPLACE = 'BUYER_MARKETPLACE',
  /**
   * Inventory management (v2): stock, SKUs, orders, further processing and the
   * floor stations. Per-component sub-flags live in the module's `settings` —
   * see `inventory-components.ts` and `useInventoryModule`.
   */
  INVENTORY = 'INVENTORY',
  /**
   * TEMPORARY (wild-game limited release, PRO-692). Read ONLY through
   * `useWildGameEnabled()` (features/wild-game) — not directly — so the
   * question "is wild game on?" has exactly one answer across the app.
   *
   * Unlike BUYER_MARKETPLACE this hides rather than merely blocks: with it
   * off, a processor sees no drop-off action, no game species in any picker,
   * and no game rows in their species setup. Enforced server-side as well;
   * the hiding is presentation, not permission.
   *
   * Remove at general availability; see
   * `packages/api/src/services/game-jobs/WILD_GAME_MODULE.md`.
   */
  WILD_GAME = 'WILD_GAME',
  /**
   * Vendor-defined job tags (icon + name + species) shown on the job page,
   * the calendar tooltip, and as icons on calendar events.
   *
   * Read ONLY through `useJobTagsEnabled()` (features/job-tags), and NOT
   * through `useIsModuleActive` — that hook answers from the module REGISTRY,
   * which only knows module types with a registered `BaseModule`. Job tags
   * own no routes and no module class, so `useIsModuleActive` would return
   * false for every vendor, forever, however the flag is set.
   */
  JOB_TAGS = 'JOB_TAGS',
  /**
   * Local Devices — the Settings page for pairing Farmshare Desktop stations.
   * Gates the nav entry and the route only; printing and scales elsewhere in
   * the app don't ask, so a paired station keeps working with it off.
   */
  LOCAL_DEVICES = 'LOCAL_DEVICES',
}
